import { colorStrings, mediaQueriesBP } from "@hardware/Utilities/variables";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import siteMessage from "@insite/client-framework/SiteMessage";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import setFieldValue from "@insite/client-framework/Store/Components/ContactUsForm/Handlers/SetFieldValue";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import { ContactFormContext } from "@insite/content-library/Widgets/ContactUs/ContactForm";
import Checkbox from "@insite/mobius/Checkbox";
import CheckboxGroup from "@insite/mobius/CheckboxGroup";
import TextField, { TextFieldPresentationProps } from "@insite/mobius/TextField";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import * as React from "react";
import { connect, ResolveThunks } from "react-redux";
import { css, ThemeProps, withTheme } from "styled-components";

const enum fields {
    title = "title",
    checkboxLabel1 = "checkboxLabel1",
    checkboxLabel2 = "checkboxLabel2",
    checkboxLabel3 = "checkboxLabel3",
    checkboxLabel4 = "checkboxLabel4",
    checkboxLabel5 = "checkboxLabel5",
    checkboxLabel6 = "checkboxLabel6",
    checkboxLabel7 = "checkboxLabel7",
}

interface OwnProps extends WidgetProps {
    fields: {
        [fields.title]: string;
        [fields.checkboxLabel1]: string;
        [fields.checkboxLabel2]: string;
        [fields.checkboxLabel3]: string;
        [fields.checkboxLabel4]: string;
        [fields.checkboxLabel5]: string;
        [fields.checkboxLabel6]: string;
        [fields.checkboxLabel7]: string;
    };
}

const mapStateToProps = (state: ApplicationState) => ({
    barCodesScanning: state.components.contactUsForm.fieldValues["barCodesScanning"],
    customLabels: state.components.contactUsForm.fieldValues["customLabels"],
    cutTubing: state.components.contactUsForm.fieldValues["cutTubing"],
    electromechanicalAssembly: state.components.contactUsForm.fieldValues["electromechanicalAssembly"],
    hotStampMarking: state.components.contactUsForm.fieldValues["hotStampMarking"],
    kitting: state.components.contactUsForm.fieldValues["kitting"],
    terminalBlock: state.components.contactUsForm.fieldValues["terminalBlock"],
});

const mapDispatchToProps = {
    setFieldValue,
};

type Props = OwnProps & ReturnType<typeof mapStateToProps> & ResolveThunks<typeof mapDispatchToProps>;

export interface AddedServicesStyles {
    addedServicesTextField?: TextFieldPresentationProps;
    title?: TypographyPresentationProps;
    checkboxwrapper?: InjectableCss;
    checkboxstyle?: InjectableCss;
    checkbox2style?: InjectableCss;
}

export const addedServicesStyles: AddedServicesStyles = {
    title: {
        variant: "p",
        css: css`
            font-size: 15px;
            font-weight: 600;
        `,
    },
    checkboxwrapper: {
        css: css`
            display: flex;
            justify-content: flex-start;
            margin: 0 -15px 15px;
            flex-wrap: wrap;
            padding: 0 15px;
            flex-direction: column;
            align-items: baseline;

            ${mediaQueriesBP.maxMD} {
                flex-direction: column;
                align-items: baseline;
            }
            
            & legend {
                margin-bottom: 0 !important;
             }
        `,
    },
    checkboxstyle: {
        css: css`
            margin-top: 0 !important;
            width: calc(33.33%);
            ${mediaQueriesBP.maxMD} {
                width: 100%;
                padding-bottom: 5px;
            }
        `,
    },
};

const styles = addedServicesStyles;

const AddedServices: React.FC<Props> = ({ fields, setFieldValue }) => {
    const {
        title,
        checkboxLabel1,
        checkboxLabel2,
        checkboxLabel3,
        checkboxLabel4,
        checkboxLabel5,
        checkboxLabel6,
        checkboxLabel7,
    } = fields;
    const [isBarCodesScanning, setBarCodesScanning] = React.useState(false);
    const [isCustomLabels, setCustomLabels] = React.useState(false);
    const [isCutTubing, setCutTubing] = React.useState(false);
    const [isElectromechanicalAssembly, setElectromechanicalAssembly] = React.useState(false);
    const [isHotStampMarking, setHotStampMarking] = React.useState(false);
    const [isKitting, setKitting] = React.useState(false);
    const [isTerminalBlock, setTerminalBlock] = React.useState(false);
    const valueAddedServices: string[] = [];

    React.useEffect(() => {
        if (isBarCodesScanning) {
            valueAddedServices.push("Bar Codes & Scanning");
        } else {
            const index = valueAddedServices.indexOf("Bar Codes & Scanning");
            if(index > -1){valueAddedServices.splice(index, 1);}            
        }

        if (isCustomLabels) {
            valueAddedServices.push("Custom Labels");
        } else {
            const index = valueAddedServices.indexOf("Custom Labels");
            if(index > -1){valueAddedServices.splice(index, 1);}
        }

        if (isCutTubing) {
            valueAddedServices.push("Cut Tubing");
        } else {
            const index = valueAddedServices.indexOf("Cut Tubing");
            if(index > -1){valueAddedServices.splice(index, 1);}
        }

        if (isElectromechanicalAssembly) {
            valueAddedServices.push("Electromechanical Assembly");
        } else {
            const index = valueAddedServices.indexOf("Electromechanical Assembly");
            if(index > -1){valueAddedServices.splice(index, 1);}
        }

        if (isHotStampMarking) {
            valueAddedServices.push("Hot Stamp Marking");
        } else {
            const index = valueAddedServices.indexOf("Hot Stamp Marking");
            if(index > -1){valueAddedServices.splice(index, 1);}
        }

        if (isKitting) {
            valueAddedServices.push("Kitting");
        } else {
            const index = valueAddedServices.indexOf("Kitting");
            if(index > -1){valueAddedServices.splice(index, 1);}
        }

        if (isTerminalBlock) {
            valueAddedServices.push("Terminal Block Modification");
        } else {
            const index = valueAddedServices.indexOf("Terminal Block Modification");
            if(index > -1){valueAddedServices.splice(index, 1);}
        }

        setFieldValue({ key: "valueAddedServices", value: valueAddedServices.toString() });

    }, [isBarCodesScanning, isCustomLabels, isCutTubing, isElectromechanicalAssembly,isHotStampMarking,isKitting,isTerminalBlock]);


    return (
        <StyledWrapper>
            <Typography {...styles.title}>{title}</Typography>
            <CheckboxGroup {...styles.checkboxwrapper}>
                <Checkbox
                    {...styles.checkboxstyle}
                    onChange={(e, value) => setBarCodesScanning(value)}
                    checked={isBarCodesScanning}
                >
                    {checkboxLabel1}
                </Checkbox>
                <Checkbox
                    {...styles.checkboxstyle}
                    onChange={(e, value) => setCustomLabels(value)}
                    checked={isCustomLabels}
                >
                    {checkboxLabel2}
                </Checkbox>
                <Checkbox {...styles.checkboxstyle} onChange={(e, value) => setCutTubing(value)} checked={isCutTubing}>
                    {checkboxLabel3}
                </Checkbox>

                <Checkbox
                    {...styles.checkboxstyle}
                    onChange={(e, value) => setElectromechanicalAssembly(value)}
                    checked={isElectromechanicalAssembly}
                >
                    {checkboxLabel4}
                </Checkbox>
                <Checkbox
                    {...styles.checkboxstyle}
                    onChange={(e, value) => setHotStampMarking(value)}
                    checked={isHotStampMarking}
                >
                    {checkboxLabel5}
                </Checkbox>
                <Checkbox {...styles.checkboxstyle} onChange={(e, value) => setKitting(value)} checked={isKitting}>
                    {checkboxLabel6}
                </Checkbox>
                <Checkbox
                    {...styles.checkboxstyle}
                    onChange={(e, value) => setTerminalBlock(value)}
                    checked={isTerminalBlock}
                >
                    {checkboxLabel7}
                </Checkbox>
            </CheckboxGroup>
        </StyledWrapper>
    );
};

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps, mapDispatchToProps)(AddedServices),
    definition: {
        group: "Contact Us",
        icon: "input-pipe",
        fieldDefinitions: [
            {
                name: fields.title,
                displayName: "Title",
                editorTemplate: "TextField",
                fieldType: "Translatable",
                defaultValue: "I am interested in the following Value Added Services:",
                isRequired: true,
            },
            {
                name: fields.checkboxLabel1,
                displayName: "Checkbox Label 1",
                editorTemplate: "TextField",
                fieldType: "Translatable",
                defaultValue: "Bar Codes & Scanning",
                isRequired: true,
            },
            {
                name: fields.checkboxLabel2,
                displayName: "Checkbox Label 2",
                editorTemplate: "TextField",
                fieldType: "Translatable",
                defaultValue: "Custom Labels",
                isRequired: true,
            },
            {
                name: fields.checkboxLabel3,
                displayName: "Checkbox Label 3",
                editorTemplate: "TextField",
                fieldType: "Translatable",
                defaultValue: "Cut Tubing",
                isRequired: true,
            },
            {
                name: fields.checkboxLabel4,
                displayName: "Checkbox Label 4",
                editorTemplate: "TextField",
                fieldType: "Translatable",
                defaultValue: "Electromechanical Assembly",
                isRequired: true,
            },
            {
                name: fields.checkboxLabel5,
                displayName: "Checkbox Label 5",
                editorTemplate: "TextField",
                fieldType: "Translatable",
                defaultValue: "Hot Stamp Marking",
                isRequired: true,
            },
            {
                name: fields.checkboxLabel6,
                displayName: "Checkbox Label 6",
                editorTemplate: "TextField",
                fieldType: "Translatable",
                defaultValue: "Kitting",
                isRequired: true,
            },
            {
                name: fields.checkboxLabel7,
                displayName: "Checkbox Label 7",
                editorTemplate: "TextField",
                fieldType: "Translatable",
                defaultValue: "Terminal Block Modification",
                isRequired: true,
            },
        ],
    },
};

export default widgetModule;
